import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Link2, Users, Coins, TrendingUp, Info } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";


export default function Affiliate() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const d = await api.affiliateStats();
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load affiliate stats");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const publicUrl = useMemo(() => {
        const code = data?.affiliate?.affiliate_code;
        if (!code) return "";
        return `${window.location.origin}?ref=${code}`;
    }, [data]);

    const copy = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(publicUrl);
            } else {
                // Fallback for insecure contexts / permission denied
                const ta = document.createElement("textarea");
                ta.value = publicUrl;
                ta.style.position = "fixed";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            toast.success("Link copiato");
        } catch (e) {
            // Last resort: show the link so the user can copy manually
            toast.error("Copia manualmente: " + publicUrl);
        }
    };

    return (
        <SidebarLayout>
            <div className="max-w-5xl mx-auto py-8 px-4">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold">Affiliate program</h1>
                        <p className="text-sm text-text-secondary">
                            30% ricorrente su tutti i pagamenti dei referral. Min payout $20.
                        </p>
                    </div>
                </div>

                {loading || !data ? (
                    <p className="text-text-secondary text-sm">Loading…</p>
                ) : (
                    <>
                        {/* Link block */}
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                            data-testid="affiliate-link-card"
                        >
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary flex items-center gap-2">
                                <Link2 className="h-3 w-3 text-[var(--color-secondary)]" />
                                Il tuo link personale
                            </p>
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <code
                                    data-testid="affiliate-link"
                                    className="flex-1 min-w-0 text-sm truncate rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 tabular-nums"
                                >
                                    {publicUrl}
                                </code>
                                <button
                                    data-testid="affiliate-copy"
                                    onClick={copy}
                                    className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] px-4 py-2 text-xs font-medium text-black hover:opacity-90"
                                >
                                    <Copy className="h-3.5 w-3.5" /> Copia
                                </button>
                            </div>
                            <p className="mt-3 text-[11px] text-text-secondary flex items-start gap-1.5">
                                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                Chi apre il link e si registra viene automaticamente attribuito a te.
                                Guadagni <strong className="text-text-main">30%</strong> ricorrente su
                                ogni loro pagamento (subscription rinnovi + credit pack).
                            </p>
                        </motion.div>

                        {/* KPIs */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                            <KPI
                                testid="affiliate-clicks"
                                icon={<Link2 className="h-3.5 w-3.5" />}
                                label="Clicks"
                                value={data.kpi.clicks}
                            />
                            <KPI
                                testid="affiliate-signups"
                                icon={<Users className="h-3.5 w-3.5" />}
                                label="Signups"
                                value={data.kpi.signups}
                            />
                            <KPI
                                testid="affiliate-mrr"
                                icon={<TrendingUp className="h-3.5 w-3.5" />}
                                label="MRR generato"
                                value={`$${data.kpi.mrr_generated_eur.toFixed(2)}`}
                            />
                            <KPI
                                testid="affiliate-earnings"
                                icon={<Coins className="h-3.5 w-3.5" />}
                                label="Total earnings"
                                value={`$${data.kpi.total_earnings_eur.toFixed(2)}`}
                            />
                        </div>

                        {/* Balance breakdown */}
                        <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Payout breakdown
                            </p>
                            <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <p className="text-text-secondary text-xs">Pending (30gg)</p>
                                    <p
                                        data-testid="affiliate-pending"
                                        className="mt-1 text-lg font-semibold tabular-nums"
                                    >
                                        ${data.kpi.pending_payout_eur.toFixed(2)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-text-secondary text-xs">Approved</p>
                                    <p
                                        data-testid="affiliate-approved"
                                        className="mt-1 text-lg font-semibold text-[var(--color-success)] tabular-nums"
                                    >
                                        ${data.kpi.approved_payout_eur.toFixed(2)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-text-secondary text-xs">Paid out</p>
                                    <p
                                        data-testid="affiliate-paid"
                                        className="mt-1 text-lg font-semibold text-text-secondary tabular-nums"
                                    >
                                        ${data.kpi.paid_out_eur.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Conversions table */}
                        <div className="mt-8" data-testid="affiliate-conversions">
                            <h3 className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Ultime conversions
                            </h3>
                            <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {data.conversions.length === 0 ? (
                                    <p className="p-6 text-sm text-text-secondary">
                                        Nessuna conversione ancora — condividi il tuo link per iniziare.
                                    </p>
                                ) : (
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                                <th className="px-4 py-3">Data</th>
                                                <th className="px-4 py-3">Evento</th>
                                                <th className="px-4 py-3">Amount</th>
                                                <th className="px-4 py-3">Commission</th>
                                                <th className="px-4 py-3">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.conversions.map((c) => (
                                                <tr
                                                    key={c.id}
                                                    className="border-b border-[var(--color-border)] last:border-0"
                                                >
                                                    <td className="px-4 py-3 text-text-secondary text-xs">
                                                        {new Date(c.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 text-text-secondary text-xs">
                                                        {c.event_type.replace(/_/g, " ")}
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums">
                                                        ${c.event_amount_eur.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 tabular-nums text-[var(--color-success)]">
                                                        ${c.commission_eur.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <StatusPill status={c.status} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="mt-10 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5" data-testid="affiliate-promo-kit">
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Promo kit
                            </p>
                            <p className="mt-1 text-xs text-text-secondary leading-relaxed">
                                3 banner pronti all'uso. Aspect ratio dei file leggermente diverso
                                dai nominali ad-network (sotto ogni banner l'indicazione precisa);
                                per network strict-format usa un editor tuo per crop finale.
                            </p>
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                <BannerCard
                                    src="/affiliate-banners/banner_1200x628.jpeg"
                                    label="Feed social (LI/FB)"
                                    nominal="1200×628"
                                    real="~1264×848 · aspect ≈ 1.49"
                                    filename="glms_affiliate_1200x628.jpeg"
                                    testid="banner-feed"
                                />
                                <BannerCard
                                    src="/affiliate-banners/banner_728x90.jpeg"
                                    label="Leaderboard display"
                                    nominal="728×90"
                                    real="~1264×848 · usa crop centrale"
                                    filename="glms_affiliate_728x90.jpeg"
                                    testid="banner-leaderboard"
                                />
                                <BannerCard
                                    src="/affiliate-banners/banner_300x250.jpeg"
                                    label="Medium rectangle"
                                    nominal="300×250"
                                    real="1024×1024 · quadrato"
                                    filename="glms_affiliate_300x250.jpeg"
                                    testid="banner-medrec"
                                />
                            </div>
                        </div>

                        <div className="mt-6 text-[10px] text-text-secondary leading-relaxed">
                            <strong className="text-text-main">Terms:</strong> 30% ricorrente su
                            subscription e credit pack di ogni utente referred. Min payout ${data.min_payout_eur.toFixed(0)}.
                            Approvazione automatica dopo 30gg (finestra rimborso Stripe). Anti-frode:
                            self-referral e same-IP-hash bloccati automaticamente. Rimborsi Stripe
                            annullano la commissione relativa.
                        </div>
                    </>
                )}
            </div>
        </SidebarLayout>
    );
}

function KPI({ icon, label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                {icon}
                {label}
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
        </div>
    );
}

function StatusPill({ status }) {
    const c =
        status === "paid"
            ? "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]"
            : status === "approved"
              ? "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
              : status === "voided"
                ? "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 text-[var(--color-alert)]"
                : "border-[var(--color-border)] text-text-secondary";
    return (
        <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${c}`}
        >
            {status}
        </span>
    );
}


function BannerCard({ src, label, nominal, real, filename, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] overflow-hidden flex flex-col"
        >
            <div className="aspect-video bg-black grid place-items-center overflow-hidden">
                <img
                    src={src}
                    alt={label}
                    className="w-full h-full object-contain"
                    loading="lazy"
                />
            </div>
            <div className="p-3">
                <p className="text-xs font-medium">{label}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-widest text-text-secondary">
                    Target: {nominal}
                </p>
                <p className="mt-1 text-[10px] text-text-secondary/70">Actual: {real}</p>
                <a
                    href={src}
                    download={filename}
                    data-testid={`${testid}-download`}
                    className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] px-3 py-1.5 text-[11px] font-medium text-black hover:opacity-90"
                >
                    Download
                </a>
            </div>
        </div>
    );
}
